import React from "react"
import { AutoComplete, Col, Drawer, Form, Input, InputNumber, Row, Switch } from "antd"
import { Button } from "src/components/actions/button"
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg"
import { TranslatableInput } from "src/components/Translatable"
import { useAppData } from "src/store/app-data"
import {
  getFormattedLangData,
  getFormData,
  getInitialValues,
  getNestedFormData,
  whiteSpaceValidation,
} from "src/utils/helpers"
import styled from "styled-components"

const DEFAULT_VALUES = {
  type: "",
  link: "",
  image: "",
  isActive: true,
  showInMobile: true,
  showInSite: true,
  sortWeight: 0,
}

export function EditDrawer({ title, initialValues, onClose, onSave, typeOptions }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)
  const [form] = Form.useForm()

  const getDefaultFormValues = React.useCallback(() => {
    if (!initialValues) {
      return DEFAULT_VALUES
    }

    return { ...DEFAULT_VALUES, ...getInitialValues(initialValues) }
  }, [initialValues])

  React.useEffect(() => {
    form.resetFields()
    form.setFieldsValue(getDefaultFormValues())
  }, [form, getDefaultFormValues, initialValues])

  function prepareData(values) {
    return {
      ...(initialValues && { _method: "PUT" }),
      type: values.type,
      link: values.link,
      image: values.image ?? "",
      isActive: values.isActive ? 1 : 0,
      showInMobile: values.showInMobile ? 1 : 0,
      showInSite: values.showInSite ? 1 : 0,
      sortWeight: values.sortWeight ?? 0,
    }
  }

  async function onSubmit(values) {
    const formData = getFormData({ values: prepareData(values) })
    const langData = getFormattedLangData({ name: values.name })
    const formDataWithLang = getNestedFormData({ name: langData.name }, formData)

    try {
      await onSave(formDataWithLang, initialValues?.id)
      onClose()
      form.resetFields()
    } catch (e) {
      console.warn("save support channel failed", e)
    }
  }

  function handleClose() {
    onClose()
    form.resetFields()
  }

  const id = initialValues?.id ?? "new"

  return (
    <DrawerEdit
      width={layoutConfig.isMobile ? "90%" : "60%"}
      title={title}
      onClose={handleClose}
      open
    >
      <FormEdit form={form} layout={"vertical"} onFinish={(values) => onSubmit(values)}>
        <StyledFormItem label={"Название"}>
          <TranslatableInput name={"name"} id={id} />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_type"}
          label={"Тип канала"}
          name={"type"}
          extra={
            "По типу приложение и сайт подбирают иконку и способ открытия. " +
            "Можно ввести свой тип — клиент откроет ссылку как есть."
          }
          rules={[
            { required: true, message: "Укажите тип канала!" },
            { validator: whiteSpaceValidation },
            {
              pattern: /^[a-z0-9_-]+$/,
              message: "Только латиница в нижнем регистре, цифры, дефис и подчёркивание",
            },
          ]}
        >
          <AutoComplete
            options={typeOptions}
            filterOption={(input, option) =>
              String(option?.value ?? "").includes(input.toLowerCase())
            }
            placeholder={"max"}
          />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_link"}
          label={"Ссылка / номер"}
          name={"link"}
          extra={"Например: https://max.ru/bushe_support, @bushe_delivery_bot, +7 (931) 109-20-10"}
          rules={[
            { required: true, message: "Укажите ссылку или номер!" },
            { validator: whiteSpaceValidation },
          ]}
        >
          <Input placeholder={"https://max.ru/bushe_support"} />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_image"}
          label={"Ссылка на иконку"}
          name={"image"}
          extra={"Необязательно: если пусто, клиент нарисует иконку по типу канала"}
        >
          <Input placeholder={"https://..."} />
        </StyledFormItem>

        <StyledFormItem
          key={id + "_sortWeight"}
          label={"Позиция в списке"}
          name={"sortWeight"}
          rules={[{ required: true, message: "Введите позицию!" }]}
        >
          <InputNumber min={0} style={{ width: "100%" }} placeholder={"0"} />
        </StyledFormItem>

        <Row gutter={[16, 16]}>
          <Col span={8}>
            <StyledFormItem
              key={id + "_isActive"}
              label={"Показывать клиентам"}
              name={"isActive"}
              valuePropName={"checked"}
            >
              <Switch />
            </StyledFormItem>
          </Col>
          <Col span={8}>
            <StyledFormItem
              key={id + "_showInMobile"}
              label={"В приложении"}
              name={"showInMobile"}
              valuePropName={"checked"}
            >
              <Switch />
            </StyledFormItem>
          </Col>
          <Col span={8}>
            <StyledFormItem
              key={id + "_showInSite"}
              label={"На сайте"}
              name={"showInSite"}
              valuePropName={"checked"}
            >
              <Switch />
            </StyledFormItem>
          </Col>
        </Row>

        <ButtonRow>
          <ButtonCol>
            <Button
              danger
              onClick={() => form.setFieldsValue(getDefaultFormValues())}
              icon={<RotateClockwiseSvg />}
            >
              Вернуть
            </Button>
          </ButtonCol>
          <ButtonCol>
            <Button onClick={handleClose} icon={<CloseSvg />}>
              Закрыть
            </Button>
          </ButtonCol>
          <ButtonCol>
            <Button icon={<SaveSvg />} type="primary" htmlType="submit">
              Сохранить
            </Button>
          </ButtonCol>
        </ButtonRow>
      </FormEdit>
    </DrawerEdit>
  )
}

const DrawerEdit = styled(Drawer)``

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`
